import { useState, useEffect, useRef, useMemo, useCallback, Fragment } from 'react';
import { Outlet, useLoaderData, useNavigate, useRouteLoaderData, useParams, Navigate, useSubmit, useRevalidator } from 'react-router';
import { Header, CircularMenuButton, Scrollable, useIsFormSubmitting, useHandleSubmitError, Actionbar, ActionbarLeft, ActionbarRight, useIsReloading, InlineHeader, InlineSection } from '@/components/DashboardComponents'
import { MessageItem } from '@/pages/SessionLayout';
import { FormProvider, FormSubmitProvider, FormObjectGroup } from '@/form/FormProvider';
import { Button, ComboButton, Spacing, Textbox, TextboxGroup, Dropdown, DropdownOption, DropdownHeading, Checkbox, RadioGroup, RadioOption } from '@/form/FormComponents';
import { GenericDropdown } from '@/components/GenericDropdown';
import { Icon } from '@/components/Icon';
import { GenericEmptyListPlaceholder } from '@/DashboardLayout';
import { timeSince, timeTaken, formatDate, shortDateTime } from '@/shared/utils';
import { SaveTranscriptButton, DiagnosesAndDifferentialsForm } from '@/pages/SessionLayout';
import { returnSubmissions } from '@/data/GraderData'
import { useErrorModal, useModal, Modal, ModalContent, ModalButtons } from '@/components/Modal';
import { useMenu } from '@/components/Menu';
import { useContinueUnlessBlocked, useShouldBlock } from '@/systems/Blocker';
import { useIsDeveloper, usePrivacyMode } from '@/systems/Authentication';
import { useExportPDFModal } from '@/components/ExportPDFModal';
import Lottie from "lottie-react";
import sparklesAnimation from '@/../assets/icons8-sparkles.json';
import { useDatabaseSync } from '@/systems/DatabaseSync';
import { useDate } from '@/hooks/useDate';
import { useIsMobile, useBreakpoint } from '@/hooks/useIsMobile';
import { useUser } from '@/systems/Authentication';
import { publicAction } from '@/data/loaders';

function StudentDropdownItem({ student, due }) {
  const privacyMode = usePrivacyMode();

  return (
    <div className="student_dropdown_item">
      <div><StudentCompletionIcon session={student.session} due={due}/></div>
      <div>{privacyMode ? "[Learner name]" : (student.name || "(No name)")}</div>
    </div>
  )
}

function PlaceholderDropdownItem() {
  return (
    <div className="student_dropdown_item">
      <div></div>
      <div>{"Select learner..."}</div>
    </div>
  )
}

function StudentCompletionIcon({ session, due }) {
  return session ? (
    session.submitted ? (
      session.returned ? (
        (due && due < session.submitted_date) ? (
          <Icon name="check-clock" style={{fill: "var(--submission_graded)"}}/>
        ) : (
          <Icon name="check-circle" style={{fill: "var(--submission_graded)"}}/>
        )
      ) : (
        (due && due < session.submitted_date) ? (
          <Icon name="check-outline-clock" style={{fill: "var(--submission_graded)"}}/>
        ) : (
          <Icon name="check-circle-outline" style={{fill: "var(--submission_graded)"}}/>
        )
      )
    ) : (
      <Icon name="progress-clock" style={{fill: "var(--submission_in_progress)"}}/>
    )
  ) : (
    <Icon name="dotted-circle" style={{fill: "var(--submission_not_started)"}}/>
  );
}

export function GraderLayoutFrame() {
  const navigate = useNavigate();
  const continueUnlessBlocked = useContinueUnlessBlocked();
  
  const grader = useLoaderData().data.value;
  const assessment = grader.assessment;
  const params = useParams();

  const just_students = useDatabaseSync({ query: {collection: "course_memberships", course_id: assessment.course_id}, original_value: grader.students, enabled: true }).filter(a => !a.role || a.role == "student").map(a => {
    const session = grader.students.find(b => b._id == a._id)?.session;
    return {...a, session: session || a.session};
  });
  const sessions = useDatabaseSync({ query: {collection: "sessions", assessment_id: assessment._id}, original_value: [], enabled: true });

  const students = just_students.map(a => {
    const session = sessions.find(b => b.uid == a._id);
    return {...a, session: session || a.session};
  }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const this_student_has_session = students.find(a => a._id == params.student_uid)?.session;
  const prev_session = useRef(this_student_has_session);
  const prev_student = useRef(params.student_uid);
  useEffect(() => {
    if (this_student_has_session && !prev_session.current && params.student_uid === prev_student.current) {
      navigate(params.student_uid, {replace: true});
    }
    prev_session.current = this_student_has_session;
    prev_student.current = params.student_uid;
  }, [this_student_has_session, navigate, params.student_uid]);

  const items = students.map(a => {return {name: a._id, element: <StudentDropdownItem student={a} due={assessment.data.due_date}/>, height: 40}});
  const selected_student_id = students.find(a => a._id == params.student_uid)?._id;
  const selected_student_index = students.findIndex(a => a._id == selected_student_id);
  const selected_student = students.find(a => a._id == selected_student_id);

  const handleChange = (value) => {
    if (value !== selected_student_id) {
      continueUnlessBlocked(() => {
        navigate(value, {replace: true});
      });
    }
  }

  const hasPrevious = selected_student_index > 0;

  const handlePrevious = () => {
    const index = selected_student_index;
    if (index > 0) {
      navigate(students[index-1]._id, {replace: true});
    }
  }

  const hasNext = selected_student_index < students.length-1;

  const handleNext = () => {
    const index = selected_student_index;
    if (index < students.length-1) {
      navigate(students[index+1]._id, {replace: true});
    }
  }

  const shouldBlock = useShouldBlock({type: "forms"});
  const [openErrorModal] = useErrorModal();
  const [openReturnSubmissionsModal] = useModal(({ context, closeModal, setLoading }) => {
    function submitModal() {
      setLoading(true);
  
      returnSubmissions({ assessment_id: grader.assessment._id, course_id: grader.assessment.course_id, student_uids: context.student_uids }).then(function(data) {
        closeModal();
        navigate(selected_student_id || ".", { replace: true });
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    if (shouldBlock()) {
      return (
        <Modal title={"You have unsaved changes"}>
          <ModalContent>
            {"Save your changes before returning this submission."}
          </ModalContent>
          <ModalButtons>
            <Button text="Got it" className="" onClick={closeModal}/>
          </ModalButtons>
        </Modal>
      )
    }

    return (
      <Modal title={context.type == "single" ? "Return this submission?" : `Return all${context.student_uids.length > 1 ? ` ${context.student_uids.length}` : ''} completed submissions?`}>
        <ModalContent>
          {`The learner${context.type == "single" ? '' : 's'} will be notified that new feedback is available.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Return" className="primary" onClick={submitModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const { handleOnClick } = useMenu({
    menuItems: [
      {
        label: 'Return this submission',
        onClick: () => {openReturnSubmissionsModal({type: "single", student_uids: [selected_student._id]})},
        disabled: !selected_student?.session?.submitted || selected_student?.session?.returned,
      },
      {
        label: 'Return all completed submissions',
        onClick: () => {openReturnSubmissionsModal({type: "all_completed", student_uids: students.filter(a => a.session?.submitted && !a.session?.returned).map(a => a._id)})},
        disabled: students.filter(a => a.session?.submitted && !a.session?.returned).length == 0,
      }
    ]
  });

  const hide_stats = useBreakpoint((width) => width <= 900);

  const wrap_student_controls = useBreakpoint((width) => width <= 660);

  const student_controls = (
    <div className="grader_student_controls">
      <CircularMenuButton onClick={() => {continueUnlessBlocked(handlePrevious)}} disabled={!hasPrevious} icon="chevron-left"/>
      <GenericDropdown items={items} menuPaddingHeight={10} value={selected_student_id} default_element={<PlaceholderDropdownItem/>} onChange={handleChange} className="grader_student_dropdown"/>
      <CircularMenuButton onClick={() => {continueUnlessBlocked(handleNext)}} disabled={!hasNext} icon="chevron-right"/>
    </div>
  )

  return (
    <div className="dashboard_container">
      <header className="dashboard_topbar">
        <div className="topbar_left">
          <CircularMenuButton onClick={() => navigate(-1)} icon="arrow-left"/>
          <div className="topbar_title">{assessment.data.title}</div>
        </div>
        <div className="topbar_right">
          {!hide_stats && <div className="grader_stats">
            {/* {
              (grader.total_cost != null && false) ? (
                <>
                  <div>
                    <div>{formatCost(grader.input_cost)}</div>
                    <div>Input cost</div>
                  </div>
                  <div>
                    <div>{formatCost(grader.output_cost)}</div>
                    <div>Output cost</div>
                  </div>
                  <div>
                    <div>{formatCost(grader.total_cost)}</div>
                    <div>Total cost</div>
                  </div>
                </>
              ) : null
            } */}
            <div>
              <div>{(students.filter(a => !a.session?.submitted).length)}</div>
              <div>Assigned</div>
            </div>
            <div>
              <div>{(students.filter(a => a.session?.submitted && !a.session?.returned).length)}</div>
              <div>Completed</div>
            </div>
            <div>
              <div>{(students.filter(a => a.session?.submitted && a.session?.returned).length)}</div>
              <div>Returned</div>
            </div>
          </div>}
          <div className="topbar_button">
            <ComboButton>
              <Button text="Return" className="primary" disabled={!selected_student?.session?.submitted || selected_student?.session?.returned} onClick={() => {openReturnSubmissionsModal({type: "single", student_uids: [selected_student._id]})}}/>
              <Button icon="menu-down-enlarged" className="primary" onClick={handleOnClick}/>
            </ComboButton>
          </div>
          {!wrap_student_controls && student_controls}
        </div>
      </header>
      {wrap_student_controls && (
        <header className="dashboard_secondary_topbar">
          {student_controls}
        </header>
      )}
      <div className="dashboard_main">
        {
          params.student_uid ? (
            selected_student_id ? <Outlet key={selected_student_id} /> : <GenericEmptyListPlaceholder text={"This learner isn't enrolled in the course anymore."}/>
          ) : (
            students.length > 0 ? <Navigate to={students[0]._id} replace /> : <GenericEmptyListPlaceholder text={"There aren't any learners enrolled in the course yet."}/>
          )
        }
      </div>
    </div>
  )
}

export function ReviewLayoutFrame({from}) {
  const navigate = useNavigate();
  const params = useParams();
  const review = useLoaderData().data.value;
  const assessment = review.assessment;
  const session = review.session;
  const session_id = review.session_id;
  const { uid } = useUser();

  const course = useRouteLoaderData("course")?.data?.value;
  if (from === "assessment" && course?.membership_type === "educator") {
    return <Navigate to={`/courses/${params.course_id}/assessments/${params.assessment_id}/grader`} replace />;
  }

  if (from == "session" && !session.preview) {
    return <Navigate to={"/courses/"+session.course_id+"/assessments/"+session.assessment_id+"/review"} replace />
  }

  return (
    <div className="dashboard_container">
      <header className="dashboard_topbar">
        <div className="topbar_left">
          <CircularMenuButton onClick={() => navigate(session.demo ? -2 : -1)} icon="arrow-left"/>
          <div className="topbar_title">{assessment ? assessment.data.title : session.scenario_data.title}</div>
        </div>
        <div className="topbar_right">
          {(!session.submitted && session.uid === uid) && <div className="topbar_button">
            <Button text="Resume" icon="play-outline" className="hollow" onClick={() => {navigate(`/session/${session_id}`)}}/>
          </div>}
        </div>
      </header>
      <div className="dashboard_main">
        {
          (session?.submitted || session?.preview) ? <ReviewSubmissionLayout is_student_review={!session.preview} can_edit_feedback={session.preview && session.uid === uid} due={assessment ? assessment.data.due_date : null} demo={session.demo} /> : <GenericEmptyListPlaceholder text={"You haven't submitted this assessment yet."}/>
        }
      </div>
    </div>
  )
}

export function GraderLayoutSubmission() {
  const params = useParams();
  const grader = useRouteLoaderData("grader").data.value;
  const students = grader.students;
  const privacyMode = usePrivacyMode();

  const selected_student = students.find(a => a._id == params.student_uid);

  const assessment_id = params.assessment_id;
  const course_id = params.course_id;

  const submission = useLoaderData().data.value;

  if (!submission?.session) {
    return <GenericEmptyListPlaceholder text={(privacyMode ? "[Learner name]" : (selected_student.name || "(No name)"))+" hasn't started the assessment yet."}/>
  } else {
    return <ReviewSubmissionLayout is_student_review={false} can_edit_feedback={true} student_name={selected_student?.name} due={grader.assessment.data.due_date} assessment_id={assessment_id} course_id={course_id} regenerate_all={true}/>;
  }
}

function ReviewSubmissionLayout({is_student_review = false, can_edit_feedback = false, student_name, due, demo = false, assessment_id, course_id, regenerate_all = false}) {
  const [openErrorModal] = useErrorModal();
  const revalidator = useRevalidator();
  const { uid } = useUser();

  const submit = useSubmit();
  const submitting = useIsFormSubmitting();
  const is_reloading = useIsReloading();
  useHandleSubmitError();

  const is_developer = useIsDeveloper();
  const privacyMode = usePrivacyMode();

  const submission = useLoaderData().data.value;

  const session = useDatabaseSync({ query: {collection: "sessions", session_id: submission.session_id}, original_value: submission.session, single_document_mode: true });

  const conversation_messages = useDatabaseSync({ query: {collection: "messages", session_id: submission.session_id}, original_value: submission.session.messages, enabled: !submission.session.concluded });

  const scenario_data = session.scenario_data;

  const feedback_tab = (is_student_review ? session.returned : session.submitted);

  const [activeTab, setActiveTab] = useState(feedback_tab ? "feedback": "transcript");

  const conversation_message_items = [{type: "opening_blurb", opening_blurb: scenario_data.opening_blurb, workup_to_date_summary: scenario_data.workup_to_date_summary, whos_speaking: scenario_data.whos_speaking, speaker_relationship_to_patient: scenario_data.speaker_relationship_to_patient }].concat(conversation_messages.map(a => {return {type: "message", message: (
    (privacyMode && student_name) ? {
      ...a,
      text: a?.text?.replaceAll(student_name, "[Learner name]")?.replaceAll(student_name.split(" ")[0], "[Learner name]")?.replaceAll(student_name.split(" ")[1], "[Learner name]"),
      response_text: a?.response_text?.replaceAll(student_name, "[Learner name]")?.replaceAll(student_name.split(" ")[0], "[Learner name]")?.replaceAll(student_name.split(" ")[1], "[Learner name]"),
    } : a
  )}}));

  // Differential add/rule out messages
  let differential_messages = [];
  (session.considering_differentials || []).forEach((differential, index) => {
    differential_messages.push({type: "add_differential", name: differential.rename_history?.[0]?.old_name || differential.name, why: differential.why, date: differential.date});
    (differential.rename_history || []).forEach((rename, index) => {
      differential_messages.push({type: "rename_differential", old_name: rename.old_name, new_name: rename.new_name, date: rename.date, why: rename.why});
    });
    if (differential.ruled_out) {
      differential_messages.push({type: "rule_out_differential", name: differential.name, why: differential.why_ruled_out, date: differential.date_ruled_out});
    }
  });
  differential_messages = differential_messages.sort((a, b) => a.date - b.date);

  //insert differential_messages into conversation_message_items at the correct locations based on date. For each differential_message, loop over conversation_message_items until we find one with a later date, and then insert the differential_message before that one. If we get to the end, insert at the end.
  differential_messages.forEach(differential_message => {
    let index = 0;
    while (index < conversation_message_items.length && (conversation_message_items[index].type !== "message" || conversation_message_items[index].message.date <= differential_message.date)) {
      index++;
    }
    conversation_message_items.splice(index, 0, differential_message);
  });

  // Group messages by thread
  const conversation_message_item_groups = [];

  let previousTo = null;
  let currentGroup = [];

  conversation_message_items.forEach(item => {
    let thisTo = item.type == "message" ? item.message.to : "patient";
    if (thisTo !== previousTo) {
      if (currentGroup.length > 0) {
        conversation_message_item_groups.push(currentGroup);
      }
      currentGroup = [];
    }
    currentGroup.push(item);
    previousTo = thisTo;
  });

  if (currentGroup.length > 0) {
    conversation_message_item_groups.push(currentGroup);
  }

  const [regenerate_loading, setRegenerateLoading] = useState(false);

  function handleRegenerateFeedback(regenerate_all_students = false, force_all) {
    setRegenerateLoading(true);
    if (regenerate_all_students) {
      publicAction("regenerateFeedbackAll", { assessment_id, course_id }).then(() => {
        revalidator.revalidate();
      }).catch((error) => {
        console.error(error);
        openErrorModal();
        setRegenerateLoading(false);
      });
    } else {
      publicAction("regenerateFeedback", { session_id: submission.session_id, force_all }).then(() => {
        revalidator.revalidate();
      }).catch((error) => {
        console.error(error);
        openErrorModal();
        setRegenerateLoading(false);
      });
    }
  }

  useEffect(() => {
    if (!is_reloading) {
      setRegenerateLoading(false);
    }
  }, [is_reloading]);

  const [openRegenerateModal] = useModal(({ closeModal }) => {
    function doRegenSubmit({ formatted_data }) {
      handleRegenerateFeedback(false, formatted_data.force_all);
      closeModal();
    }

    return (
      <Modal title="Regenerate feedback">
        <FormProvider onSubmit={doRegenSubmit}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Checkbox name="force_all" text="Regenerate all rubrics, even if feedback is up to date" default_value={true}/>
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Regenerate" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  })

  function submitForm({ formatted_data, updateData, passthrough_args }) {
    submit(formatted_data, { method: "post", encType: "application/json" });
  }

  const multiple_rubrics = useMemo(() => (session.feedback_data?.generations || []).map((a, index) => a.rubric_id ? `rubric_id_${a.rubric_id}` : `local_id_${a.rubric_local_id || index}`).filter((a, i, self) => self.indexOf(a) === i).length > 1, [session.feedback_data?.generations]);

  const feedback_generations = useMemo(() => [...(session.feedback_data?.generations || [])].sort((a, b) => {
    if (a.latest && !b.latest) return -1;
    if (!a.latest && b.latest) return 1;
    if (a.latest && b.latest) return a.sort_order - b.sort_order;
    return (a.date === b.date) ? a.sort_order - b.sort_order : b.date - a.date;
  }).map(a => [a._id, {
    title: a.latest ? (multiple_rubrics ? a.title : "Latest version") : (multiple_rubrics ? `${a.title} • ${shortDateTime(a.date)}` : shortDateTime(a.date)),
    feedback_text: (privacyMode && student_name) ? a.feedback_text.replaceAll(student_name, "[Learner name]").replaceAll(student_name.split(" ")[0], "[Learner name]").replaceAll(student_name.split(" ")[1], "[Learner name]") : a.feedback_text,
    rubric_debug: a.rubric_debug,
    latest: a.latest,
  }]), [session.feedback_data?.generations, multiple_rubrics, privacyMode, student_name]);

  const initial_data = useMemo(() => Object.fromEntries(feedback_generations.map(a => [a[0], {
    feedback_text: a[1].feedback_text,
  }])), [feedback_generations]);

  const feedback_generations_key = useMemo(() => feedback_generations.map(a => a[0]).join(","), [feedback_generations]);

  const [promptId_value, setPromptId] = useState();
  const promptId = ((promptId_value && promptId_value[1] === feedback_generations_key) ? promptId_value[0] : null) ?? feedback_generations[0]?.[0] ?? null;

  const handlePromptChange = useCallback((value) => {
    setPromptId([value, feedback_generations_key]);
  }, [feedback_generations_key]);

  const messages_scroller_ref = useRef(null);
  const scroll_lock_ref = useRef(false);
  const scroll_lock_position_ref = useRef(0);
  const previous_height_ref = useRef(0);

  useEffect(() => {
    if (messages_scroller_ref.current) {
      const container = messages_scroller_ref.current;
      const newHeight = container.scrollHeight;
      previous_height_ref.current = newHeight;

      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 91;

      if (atBottom) {
        scroll_lock_ref.current = true;
        scroll_lock_position_ref.current = container.scrollHeight - container.clientHeight;
      }
    }
  }, []);

  useEffect(() => {
    if (messages_scroller_ref.current && scroll_lock_ref.current) {
      const container = messages_scroller_ref.current;
      const newHeight = container.scrollHeight;
      const heightDiff = newHeight - previous_height_ref.current;

      const whereWeShouldAlreadyBe = newHeight - container.clientHeight - heightDiff;

      container.scrollTo({
        top: whereWeShouldAlreadyBe + Math.min(heightDiff, container.clientHeight - 91),
        behavior: 'smooth'
      });
      
      previous_height_ref.current = newHeight;
    }
  }, [conversation_message_items.length]);

  function handleMessagesScroll(event) {
    const container = event.target;
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 91;
    
    if (atBottom) {
      scroll_lock_ref.current = true;
      scroll_lock_position_ref.current = container.scrollHeight - container.clientHeight;
    } else if (container.scrollTop < scroll_lock_position_ref.current) {
      scroll_lock_ref.current = false;
      scroll_lock_position_ref.current = 0;
    }
  }

  const is_mobile = useIsMobile();

  const feedback_status = session.ai_feedback_status?.status == "completed" ? "completed" : ((session.ai_feedback_status?.status == "waiting" || session.ai_feedback_status?.status == "in_progress" || session.ai_feedback_status?.status == "in_progress_waiting") ? "generating" : (session.ai_feedback_status?.status == "error" ? "error" : null));

  return (
    <>
      <Scrollable className="dashboard_content" depencencies={[activeTab, feedback_status]}>
        <Header tabs={[
            ...(feedback_tab ? [{id: "feedback", name: "Feedback"}] : []),
            {id: "transcript", name: "Transcript"},
            ...((session.concluded && session.data && scenario_data.diagnose) ? [{id: "diagnoses", name: "Diagnoses"}] : []),
            ...((session.concluded && session.data && scenario_data.assessment_and_plan) ? [{id: "assessment_and_plan", name: "Assessment and Plan"}] : []),
            ...((session.concluded && session.data && scenario_data.questions?.length > 0) ? [{id: "questions", name: "Follow-up Questions"}] : []),
            ...(is_mobile ? [{id: "submission_details", name: "Submission details"}] : [])
          ]} activeTab={activeTab} setActiveTab={setActiveTab}></Header>
        <div style={{display: "contents"}} id="grader_feedback">
          <FormProvider initial_data={initial_data} onSubmit={submitForm} unsaved_modal="review">
            <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "feedback"} style={{display: (activeTab == "feedback" ? "" : "none")}}>
              <div className="dashboard_content_container">
                {
                  (feedback_status == "completed" || feedback_generations.length > 0) ? (
                    <>
                      {feedback_generations.length > 0 && (
                        <div className="callout_hint_box">
                          <div>
                            <div><Icon name="sparkles"/></div>
                            <div>This feedback was generated by AI, which can make mistakes.</div>
                          </div>
                          {session.feedback_data?.feedback_mode === "review" && (
                            <div>
                              <div><Icon name="account-edit"/></div>
                              <div>Your instructor had the opportunity to review and make corrections.</div>
                            </div>
                          )}
                        </div>
                      )}
                      {feedback_status === "generating" ? (
                        <div className="feedback_regenerate_banner">
                          <div>
                            <div className="sparkles_animation"><Lottie animationData={sparklesAnimation} /></div>
                            <div className="text_ai_shimmer">Generating new feedback...</div>
                          </div>
                        </div>
                      ) : (
                        feedback_status === "error" ? (
                          <div className="feedback_regenerate_banner">
                            <div>
                              <div><Icon name="alert-circle-outline"/></div>
                              <div>Something went wrong generating new feedback.</div>
                            </div>
                            <div><Button icon="refresh" text="Regenerate" className="hollow small" onClick={() => {handleRegenerateFeedback(regenerate_all)}} loading={regenerate_loading}/></div>
                          </div>
                        ) : (
                          submission._regenerate_rubrics_count > 0 && submission._regenerate_rubrics_date > (session.ai_feedback_status?.date || 0) && (
                            <div className="feedback_regenerate_banner primary">
                              <div>
                                <div><Icon name="bullseye-arrow"/></div>
                                <div>{`${submission._total_rubrics_count > 1 ? (submission._regenerate_rubrics_count > 1 ? `Some of the rubrics were changed.` : `A rubric was added or changed.`) : `The rubric was changed.`} Regenerate to see updated feedback.`}</div>
                              </div>
                              <div><Button icon="refresh" text={regenerate_all ? "Regenerate all" : "Regenerate"} className="primary small" onClick={() => {handleRegenerateFeedback(regenerate_all)}} loading={regenerate_loading}/></div>
                            </div>
                          )
                        )
                      )}
                      {(feedback_generations.length > 1 || is_developer) ? <>
                        <Dropdown value={promptId} onChange={handlePromptChange}>
                          {feedback_generations.flatMap((a, i) => [
                            ...(!a[1].latest && (i === 0 || feedback_generations[i - 1][1].latest)
                              ? [<DropdownHeading text="Older feedback (only educators can see)" key="older_heading"/>]
                              : []),
                            <DropdownOption name={a[0]} text={a[1].title} key={a[0]}/>
                          ])}
                        </Dropdown>
                        <Spacing/>
                      </> : null}
                      {feedback_generations.length > 0 ? (
                        <>
                          {feedback_generations.filter(generation => generation[1].latest).map(generation => (
                            <div style={{display: (promptId == generation[0] || feedback_generations.length == 1) ? "contents" : "none"}} key={generation[0]}>
                              <FormObjectGroup name={generation[0]}>
                                <Textbox name="feedback_text" markdown={true} fullWidth={true} readOnly={!can_edit_feedback}/>
                                <div style={{display: is_developer ? "" : "none"}}>
                                  <Spacing/>
                                  <Textbox title="Rubric" multiline={true} fullWidth={true} height={20} readOnly={true} presentation_mode={true} value={generation[1].rubric_debug}/>
                                </div>
                              </FormObjectGroup>
                            </div>
                          ))}
                          {feedback_generations.filter(generation => promptId == generation[0] && !generation[1].latest).map(generation => (
                            <Fragment key={generation[0]}>
                              <Textbox markdown={true} fullWidth={true} readOnly={true} value={generation[1].feedback_text}/>
                              <div style={{display: is_developer ? "" : "none"}}>
                                <Spacing/>
                                <Textbox title="Rubric" multiline={true} fullWidth={true} height={20} readOnly={true} presentation_mode={true} value={generation[1].rubric_debug}/>
                              </div>
                            </Fragment>
                          ))}
                        </>
                      ) : (
                        <div className="error_box">
                          <div>
                            {session.ai_feedback_status?.permission_error ? <>
                              <div><Icon name="comment-text-outline"/></div>
                              <div>{`No feedback available because ${session.uid === uid ? "you don't" : "this person doesn't"} have access to the scenario anymore.`}</div>
                            </> : (
                              (session.preview || can_edit_feedback) ? <>
                                <div><Icon name="bullseye-arrow"/></div>
                                <div>{`Add a rubric to get feedback.`}</div>
                              </> : <>
                                <div><Icon name="comment-text-outline"/></div>
                                <div>{`No feedback available`}</div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                      {(is_mobile && demo) && <>
                        <Spacing/>
                        <InlineHeader title="Next steps"/>
                        <InlineSection>
                          <DemoCaseNextSteps/>
                        </InlineSection>
                      </>}
                    </>
                  ) : (
                    feedback_status == "generating" ? (
                      <div className="error_box">
                        <div>
                          <div className="sparkles_animation"><Lottie animationData={sparklesAnimation} /></div>
                          <div className="text_ai_shimmer">Generating personalized feedback...</div>
                        </div>
                      </div>
                    ) : (
                      feedback_status == "error" ? (
                        <div className="error_box">
                          <div>
                            <div><Icon name="alert-circle-outline"/></div>
                            <div>Something went wrong</div>
                            <div>
                              <Button icon="refresh" text="Regenerate" onClick={() => {handleRegenerateFeedback(false)}} loading={regenerate_loading}/>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="error_box">
                          <div>
                            <div><Icon name="comment-text-outline"/></div>
                            <div>{`Feedback hasn't been generated yet.`}</div>
                            <div>
                              <Button icon="sparkles" className="" text="Generate" onClick={() => {handleRegenerateFeedback(false)}} loading={regenerate_loading}/>
                            </div>
                          </div>
                        </div>
                      )
                    )
                  )
                }
              </div>
            </div>
            {(can_edit_feedback && (feedback_status == "completed" || feedback_generations.length > 0) && activeTab == "feedback") ? <>
              <Actionbar>
                <ActionbarLeft>
                  {is_developer ? <Button icon="refresh" text="Regenerate" onClick={openRegenerateModal}/> : null}
                </ActionbarLeft>
                <ActionbarRight>
                  <FormSubmitProvider>
                    {({handleSubmit, has_changed}) => {
                      return <Button className="primary" text="Save changes" onClick={handleSubmit} disabled={submitting || !has_changed || is_reloading}/>
                    }}
                  </FormSubmitProvider>
                </ActionbarRight>
              </Actionbar>
            </> : null}
          </FormProvider>
        </div>
        <div className="conversation_messages_container" style={{display: (activeTab == "transcript" ? "" : "none")}}>
          <div className="conversation_messages_container_inner nearest_scrollable_container" hidden={activeTab !== "transcript"} ref={messages_scroller_ref} onScroll={handleMessagesScroll}>
            <div className="conversation_message_actions_background"></div>
            <div className="conversation_message_history">
              <MessageItemGroups conversation_message_item_groups={conversation_message_item_groups} actors={session.actors}/>
            </div>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "diagnoses"} style={{display: (activeTab == "diagnoses" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <FormProvider initial_data={session.data || {}} readOnly={true} presentation_mode={true}>
              <DiagnosesAndDifferentialsForm/>
            </FormProvider>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "assessment_and_plan"} style={{display: (activeTab == "assessment_and_plan" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <FormProvider initial_data={session.data || {}} readOnly={true} presentation_mode={true}>
              <Textbox name="treatment_plan" title="Assessment and plan" markdown={true} fullWidth={true} multiline={true}/>
            </FormProvider>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "questions"} style={{display: (activeTab == "questions" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <FormProvider initial_data={session.data || {}} readOnly={true} presentation_mode={true}>
              <TextboxGroup name="questions" textboxes={(scenario_data.questions || []).map(a => {return {title: a}})} multiline={true}/>
            </FormProvider>
          </div>
        </div>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={activeTab !== "submission_details"} style={{display: (activeTab == "submission_details" ? "" : "none")}}>
          <div className="dashboard_content_container">
            <SubmissionDetails session={session} due={due} submission={submission} conversation_message_items={conversation_message_items}/>
          </div>
        </div>
      </Scrollable>
      {!is_mobile && (!demo ? (
        <Scrollable className="dashboard_side_panel">
          <Header title="Submission details" className="mini"/>
          <div className="dashboard_side_panel_inner nearest_scrollable_container">
            <SubmissionDetails session={session} due={due} submission={submission} conversation_message_items={conversation_message_items}/>
          </div>
        </Scrollable>
      ) : (
        <Scrollable className="dashboard_side_panel">
          <Header title="Next steps" className="mini"/>
          <div className="dashboard_side_panel_inner nearest_scrollable_container">
            <DemoCaseNextSteps/>
          </div>
        </Scrollable>
      ))}
    </>
  )
}

function SubmissionDetails({ session, due, submission, conversation_message_items }) {
  const [openExportPDFModal] = useExportPDFModal();
  const [openSubmissionHistoryModal] = useModal(({ closeModal }) => {
    return (
      <Modal title="Submission history">
        <ModalContent>
          <div className="submission_history_container">
            {session.returned ? <div>
              <div>Returned</div>
              <div>{formatDate(session.returned_date)}</div>
            </div> : null}
            {session.submitted ? <div>
              <div>{due && due < session.submitted_date ? "Completed late" : "Completed"}</div>
              <div>{formatDate(session.submitted_date)}</div>
            </div> : null}
            {session.concluded ? <div>
              <div>Concluded encounter</div>
              <div>{formatDate(session.concluded_date)}</div>
            </div> : null}
            <div>
              <div>Started</div>
              <div>{formatDate(session.date)}</div>
            </div>
          </div>
        </ModalContent>
        <ModalButtons>
          <Button text="Close" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  }, [session.returned, session.submitted, session.concluded])

  const now = useDate({updateWhen: "minute_change"});

  const is_developer = useIsDeveloper();

  return (
    <>
      <div className="submission_status links">
        <div><StudentCompletionIcon session={session} due={due}/></div>
        <div>
          <div>{
            session.submitted ? (
              session.returned ? (
                (due && due < session.submitted_date) ? (
                  "Returned (completed late)"
                ) : (
                  "Returned"
                )
              ) : (
                (due && due < session.submitted_date) ? (
                  "Completed late"
                ) : (
                  "Completed"
                )
              )
            ) : (
              "In progress"
            )
          }</div>
          <div>{(!session.submitted ? "Started: " : "") + formatDate(session.submitted ? (session.returned ? session.returned_date : session.submitted_date) : session.date)}</div>
          <div><a onClick={openSubmissionHistoryModal} tabIndex={0}>See history</a></div>
        </div>
      </div>
      {submission._scenario_outdated && <div className="submission_stat">
        <div><Icon name="history"/></div>
        <div>
          <div>{"Scenario was since updated"}</div>
          <div>{"This ran using an older version"}</div>
        </div>
      </div>}
      <div className="submission_stat">
        <div><Icon name="clock-outline"/></div>
        <div><div>{session.submitted ? `Took ${timeTaken(session.submitted_date - session.date)}` : `Last active ${timeSince((Date.now() - session.date_last_active < 1000*60*5) ? Date.now() : session.date_last_active, now).toLowerCase()}`}</div></div>
      </div>
      {session.submitted ? <><br/><Button icon="file-download-outline" text="Download as PDF" className="hollow small" onClick={() => openExportPDFModal({ session_id: submission.session_id, scenario_data: session.scenario_data })}/></> : null}
      {(session.concluded && is_developer) ? <><br/><SaveTranscriptButton conversation_message_items={conversation_message_items.filter(a => !(a.type == "message" && a.message.to !== "patient"))} session_id={submission.session_id}/></> : null}
    </>
  )
}

function DemoCaseNextSteps() {
  const navigate = useNavigate();

  return (
    <>
      <div className="demo_next_step_card">
        <div>Explore the educator tools</div>
        <div>Create a new account with your institutional email for a free trial of our scenario builder and classroom management tools.</div>
        <div className="buttons right">
          <Button text="Create account" className="primary" onClick={() => {navigate("/login")}}/>
        </div>
      </div>
      <div className="demo_next_step_card">
        <div>Schedule a guided tour</div>
        <div>Interested in learning more about Sapient Clinician for your program? Let's talk.</div>
        <div className="buttons right">
          <Button text="Schedule a guided tour" className="primary" onClick={() => {window.open("https://sapientclinician.com/contact", "_blank")}}/>
        </div>
      </div>
      <div className="demo_next_step_card">
        <div>Share this demo with colleagues</div>
        <div style={{userSelect: "text"}}>sapientclinician.com/demo</div>
        <div className="buttons right">
          <CopyTextButton text={"https://sapientclinician.com/demo"}/>
        </div>
      </div>
    </>
  )
}

function CopyTextButton({text}) {
  const [justCopied, setJustCopied] = useState(false);
  const resetCopiedTimeout = useRef(null);

  const copyText = function() {
    navigator.clipboard.writeText(text);
    setJustCopied(true);

    if (resetCopiedTimeout.current) {
      clearTimeout(resetCopiedTimeout.current);
    }

    resetCopiedTimeout.current = setTimeout(() => {
      setJustCopied(false);
    }, 1500);
  }

  return <Button text={justCopied ? "Copied" : "Copy link"} icon={justCopied ? "check" : "copy"} className="hollow small" onClick={copyText}/>
}

export function MessageItemGroups({ conversation_message_item_groups, actors }) {

  let message_groups = conversation_message_item_groups.map((items, index) => {

    let to = items[0].type == "message" ? items[0].message.to : "patient";
    let message_items = items.map((item, index) => <MessageItem key={index} item={item} review={true} color={to != "patient" ? "advice" : null}/>);

    if (to == "patient") {
      return (
        <div key={index}>
          {message_items}
        </div>
      )
    } else {
      return (
        <div key={index} className="message_group color_advice">
          <div className="message_group_header">Advice consult with <span>{actors.find(a => a.id == to).name}</span></div>
          <div>
            {message_items}
          </div>
        </div>
      )
    }
  })

  return message_groups;

}